import React, { useEffect, useState } from "react";
import { CheckCircle, AlertCircle, X, Loader2, ChevronDown } from "lucide-react";
import { ordersCreate } from "../../../../../api/auth";
import { city } from "../../../../../api/City";
import { Select } from "../../components/ui/Select";

const AddOrders = () => {
  const [cities, setCities] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const initialForm = {
    client_name: "",
    client_phone: "",
    shipping_city: "",
    shipping_address: "",
    product_type: "",
    total_amount: "",
    note: "",
    local_status: "new", 
    shipping_status: "pending",
    is_exchange: 0,
    is_fragile: 0,
    is_open: 0,
  };

  const [formData, setFormData] = useState(initialForm);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setCitiesLoading(true);
        const citiesData = await city();
        setCities(citiesData || []);
      } catch (err) {
        console.error("Initial data load error", err);
        setMessage({ type: "error", text: "Failed to load initial data." });
      } finally {
        setCitiesLoading(false);
      }
    };
    loadInitialData();
  }, []);

  useEffect(() => {
    if (!message) return;
    const timer = setTimeout(() => setMessage(null), 4000);
    return () => clearTimeout(timer);
  }, [message]);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleCityChange = (value) => {
    setFormData((prev) => ({ ...prev, shipping_city: value }));
  };

  const handleStatusChange = (value) => {
    setFormData((prev) => ({ ...prev, local_status: value }));
  };

  const handleToggleChange = (field, checked) => {
    setFormData((prev) => ({ ...prev, [field]: checked ? 1 : 0 }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setMessage(null);
      await ordersCreate({
        ...formData,
        total_amount: Number(formData.total_amount),
      });
      setMessage({ type: "success", text: "Order created successfully." });
      setFormData(initialForm);
    } catch (error) {
      setMessage({
        type: "error",
        text: error?.response?.data?.detail || "Failed to create order.",
      });
    } finally {
      setLoading(false);
    }
  };

  const isInitialLoading = citiesLoading;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950 p-8 font-sans">
      <div className="mx-auto max-w-5xl rounded-2xl bg-white dark:bg-slate-900 p-8 shadow-sm">
        <h1 className="mb-6 text-2xl font-semibold text-gray-900 dark:text-slate-100">Add Manual Order</h1>

        {message && (
          <div className={`mb-6 flex items-start gap-3 rounded-xl border px-4 py-3 animate-in fade-in slide-in-from-top-2 ${
            message.type === "success" ? "border-green-200 dark:border-emerald-500/30 bg-green-50 dark:bg-emerald-500/10 text-green-700 dark:text-emerald-400" : "border-red-200 dark:border-rose-500/30 bg-red-50 dark:bg-rose-500/10 text-red-700 dark:text-rose-400"
          }`}>
            <div className="pt-0.5">
              {message.type === "success" ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
            </div>
            <div className="flex-1 text-sm font-medium">{message.text}</div>
            <button onClick={() => setMessage(null)} className="hover:opacity-70 transition-opacity"><X size={16} /></button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Client Information */}
          <div className="rounded-xl border dark:border-slate-800 bg-white dark:bg-slate-900 p-6 shadow-sm">
            <h2 className="mb-4 text-lg font-semibold text-gray-800 dark:text-slate-100 border-b dark:border-slate-800 pb-2">Client Information</h2>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              <Input label="Destinataire *" name="client_name" value={formData.client_name} onChange={handleChange} required />
              <Input label="Téléphone *" name="client_phone" value={formData.client_phone} onChange={handleChange} required />
              <div className="flex flex-col">
                <label className="mb-2 text-sm font-medium text-gray-600 dark:text-slate-400">City *</label>
                <div className="relative">
                  <Select
                    value={formData.shipping_city}
                    options={cities}
                    onChange={handleCityChange}
                    placeholder={citiesLoading ? "Loading..." : "Select city"}
                    disabled={isInitialLoading || loading}
                  />
                  {citiesLoading && <Loader2 className="absolute right-10 top-3 h-4 w-4 animate-spin text-gray-400 dark:text-slate-500" />}
                </div>
              </div>
              <div className="md:col-span-3">
                <Input label="Adresse *" name="shipping_address" value={formData.shipping_address} onChange={handleChange} required />
              </div>
            </div>
          </div>

          {/* Product & Order Status */}
          <div className="rounded-xl border dark:border-slate-800 bg-white dark:bg-slate-900 p-6 shadow-sm">
            <h2 className="mb-4 text-lg font-semibold text-gray-800 dark:text-slate-100 border-b dark:border-slate-800 pb-2">Product & Status</h2>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              <Input label="Nature du produit" name="product_type" value={formData.product_type} onChange={handleChange} />
              <Input label="Prix (MAD) *" name="total_amount" type="number" value={formData.total_amount} onChange={handleChange} required />

              <div className="flex flex-col">
                <label className="mb-2 text-sm font-medium text-gray-600 dark:text-slate-400">Order Status</label>
                <div className="relative group">
                  <select
                    name="local_status"
                    value={formData.local_status}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    disabled={loading}
                    className="w-full rounded-lg border border-gray-200 dark:border-slate-700 px-4 py-3 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 outline-none transition-all appearance-none cursor-pointer disabled:bg-gray-50 dark:disabled:bg-slate-800 disabled:cursor-not-allowed pr-10"
                  >
                    <option value="new">New</option>
                    <option value="Confirmed">Confirmed</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-3.5 h-5 w-5 text-gray-400 dark:text-slate-500 pointer-events-none group-hover:text-gray-600 dark:group-hover:text-slate-300 transition-colors" />
                </div>
              </div>
            </div>
          </div>

          {/* Delivery Options */}
          <div className="rounded-xl border dark:border-slate-800 bg-white dark:bg-slate-900 p-6 shadow-sm">
            <h2 className="mb-4 text-lg font-semibold text-gray-800 dark:text-slate-100 border-b dark:border-slate-800 pb-2">Delivery Options</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
              <ToggleCard label="Exchange" description="Autoriser l’échange" checked={!!formData.is_exchange} onChange={(checked) => handleToggleChange("is_exchange", checked)} />
              <ToggleCard label="Fragile" description="Produit fragile" checked={!!formData.is_fragile} onChange={(checked) => handleToggleChange("is_fragile", checked)} />
              <ToggleCard label="Open" description="Colis ouvrable" checked={!!formData.is_open} onChange={(checked) => handleToggleChange("is_open", checked)} />
            </div>
          </div>

          <div className="rounded-xl border dark:border-slate-800 bg-white dark:bg-slate-900 p-6 shadow-sm">
            <h2 className="mb-4 text-lg font-semibold text-gray-800 dark:text-slate-100 border-b dark:border-slate-800 pb-2">Additional Information</h2>
            <Input label="Commentaire" name="note" value={formData.note} onChange={handleChange} />
          </div>

          <div className="flex justify-end pt-4">
            <button 
              type="submit" 
              disabled={loading || isInitialLoading} 
              className="min-w-[160px] rounded-lg bg-indigo-600 px-8 py-3.5 font-semibold text-white shadow-md hover:bg-indigo-700 transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin h-5 w-5" />
                  Processing...
                </>
              ) : (
                "Create Order"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* --- UI Helpers --- */
const Input = ({ label, ...props }) => (
  <div className="flex flex-col">
    <label className="mb-2 text-sm font-medium text-gray-600 dark:text-slate-400">{label}</label>
    <input {...props} className="rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 px-4 py-3 focus:ring-2 focus:ring-indigo-500 outline-none transition-all placeholder:text-gray-300 dark:placeholder:text-slate-600" />
  </div>
);

const ToggleCard = ({ label, description, checked, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className={`flex items-center justify-between rounded-xl border p-4 text-left transition-all group ${checked ? "border-indigo-600 bg-indigo-50/50 dark:bg-indigo-500/10 shadow-sm" : "border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-gray-300 dark:hover:border-slate-700"}`}
  >
    <div className="pr-2">
      <div className={`text-sm font-bold transition-colors ${checked ? "text-indigo-900 dark:text-indigo-400" : "text-gray-800 dark:text-slate-100"}`}>{label}</div>
      <div className="mt-1 text-xs text-gray-500 dark:text-slate-500">{description}</div>
    </div>
    <div className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${checked ? "bg-indigo-600" : "bg-gray-200 dark:bg-slate-700 group-hover:bg-gray-300 dark:group-hover:bg-slate-600"}`}>
      <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-all duration-200 ${checked ? "left-5" : "left-0.5"}`} />
    </div>
  </button>
);

export default AddOrders;
